import { createSignal, onMount, onCleanup, Switch, Match, Show } from "solid-js";
import { Layout } from "./components/Layout";
import { Dashboard } from "./components/Dashboard";
import { UserPanel } from "./components/UserPanel";
import { ProjectsPanel } from "./components/ProjectsPanel";
import { AuthPanel } from "./components/AuthPanel";
import { OrganizationsPanel } from "./components/OrganizationsPanel";
import { DatabasePanel } from "./components/DatabasePanel";
import { RealtimePanel } from "./components/RealtimePanel";
import { OrganizationRegistrationRequestsPanel } from "./components/OrganizationRegistrationRequestsPanel";
import { MaintenancePanel } from "./components/MaintenancePanel";
import { TaskQueuePanel } from "./components/TaskQueuePanel";
import { ErrorsPanel } from "./components/ErrorsPanel";
import { PerformancePanel } from "./components/PerformancePanel";
import { LoginPage } from "./components/LoginPage";
import { api, type Tab, type StatusResponse, type AdminControlPlaneSnapshot, type SystemOperatorSession } from "./shared";
import { useWS } from "./useWS";
import { clearOperatorSession, getOperatorSession, isLocalDashboardHost, isOperatorSessionExpired, setOperatorSession } from "./runtime";

function hashTab(): Tab {
  const h = location.hash.slice(1);
  if (h === "organizations" || h.startsWith("organizations/")) return "organizations";
  if (h.startsWith("database/")) return "database";
  if (h.startsWith("realtime")) return "realtime";
  if (h.startsWith("performance")) return "performance";
  if (h.startsWith("task-queues")) return "task-queues";
  return (["dashboard", "users", "projects", "organizations", "registration-requests", "auth", "database", "realtime", "maintenance", "task-queues", "errors", "performance"] as Tab[]).includes(h as Tab) ? (h as Tab) : "dashboard";
}

export default function App() {
  const [tab, setTab] = createSignal<Tab>(hashTab());
  const [env, setEnv] = createSignal("local");
  const [status, setStatus] = createSignal<StatusResponse | null>(null);
  const [controlPlane, setControlPlane] = createSignal<AdminControlPlaneSnapshot | null>(null);
  const [operatorSession, setOperatorSessionState] = createSignal<SystemOperatorSession | null>(null);
  const [authLoading, setAuthLoading] = createSignal(true);
  const [authError, setAuthError] = createSignal("");

  const ws = isLocalDashboardHost()
    ? useWS(`ws://${location.host}/api/ws`)
    : (console.log("[ws] not a local dashboard host — skipping WebSocket"), {
        status: () => null as StatusResponse | null,
        connected: () => false,
        logs: () => ({} as Record<string, import("./types").LogEntry[]>),
        send: (_: unknown) => {},
      });

  onMount(() => {
    const onHash = () => setTab(hashTab());
    window.addEventListener("hashchange", onHash);
    onCleanup(() => window.removeEventListener("hashchange", onHash));

    api.controlPlane.info().then((info) => {
      setControlPlane(info);
      (window as Window & typeof globalThis).__adminConfig = info;
    }).catch(() => {});
    api.status().then((s) => { setStatus(s); setEnv(s.env); }).catch(() => {});

    const existing = getOperatorSession();
    if (!existing || isOperatorSessionExpired(existing)) {
      clearOperatorSession();
      setOperatorSessionState(null);
      setAuthLoading(false);
      return;
    }

    setAuthLoading(false);
    api.controlPlane.session().then((session) => {
      setOperatorSession(session);
      setOperatorSessionState(session);
    }).catch(() => {
      clearOperatorSession();
      setOperatorSessionState(null);
    });
  });

  const wsStatus = () => ws.status();
  onMount(() => {
    const timer = setInterval(() => {
      const s = wsStatus();
      if (s) {
        setStatus(s);
        setEnv(s.env);
        (window as any).__adminStatus = s;
      }
    }, 500);
    onCleanup(() => clearInterval(timer));
  });

  function switchTab(t: Tab) {
    setTab(t);
    location.hash = t;
  }

  async function handleServerStart(name: string, port?: number) {
    await api.servers.start(name, port);
    ws.send({ type: "get-status" });
  }

  async function handleServerStop(name: string) {
    await api.servers.stop(name);
    ws.send({ type: "get-status" });
  }

  async function handleLogin(email: string, password: string) {
    setAuthError("");
    const session = await api.controlPlane.login(email, password);
    setOperatorSession(session);
    setOperatorSessionState(session);
    return session;
  }

  async function handleLogout() {
    try {
      await api.controlPlane.logout();
    } catch {}
    clearOperatorSession();
    setOperatorSessionState(null);
    setAuthError("");
  }

  const s = () => wsStatus() || status();

  return (
    <Show
      when={!authLoading()}
      fallback={<LoginPage controlPlane={controlPlane()} loading={true} error="" onLogin={handleLogin} />}
    >
      <Show
        when={operatorSession()}
        fallback={(
          <LoginPage
            controlPlane={controlPlane()}
            loading={false}
            error={authError()}
            onLogin={async (email, password) => {
              try {
                return await handleLogin(email, password);
              } catch (error) {
                const message = error instanceof Error ? error.message : "Login failed";
                setAuthError(message);
                throw error;
              }
            }}
          />
        )}
      >
        <Layout
          tab={tab()}
          onTab={switchTab}
          env={env()}
          status={s()}
          controlPlane={controlPlane()}
          operatorSession={operatorSession()}
          onLogout={handleLogout}
          onServerStart={handleServerStart}
          onServerStop={handleServerStop}
        >
          <Switch>
            <Match when={tab() === "dashboard"}>
              <Dashboard
                status={s()}
                controlPlane={controlPlane()}
                onServerStart={handleServerStart}
                onServerStop={handleServerStop}
                logs={ws.logs()}
              />
            </Match>
            <Match when={tab() === "users"}>
              <UserPanel />
            </Match>
            <Match when={tab() === "projects"}>
              <ProjectsPanel />
            </Match>
            <Match when={tab() === "auth"}>
              <AuthPanel />
            </Match>
            <Match when={tab() === "organizations"}>
              <OrganizationsPanel />
            </Match>
            <Match when={tab() === "registration-requests"}>
              <OrganizationRegistrationRequestsPanel />
            </Match>
            <Match when={tab() === "database"}>
              <DatabasePanel />
            </Match>
            <Match when={tab() === "realtime"}>
              <RealtimePanel />
            </Match>
            <Match when={tab() === "maintenance"}>
              <MaintenancePanel />
            </Match>
            <Match when={tab() === "task-queues"}>
              <TaskQueuePanel />
            </Match>
            <Match when={tab() === "errors"}>
              <ErrorsPanel />
            </Match>
            <Match when={tab() === "performance"}>
              <PerformancePanel />
            </Match>
          </Switch>
        </Layout>
      </Show>
    </Show>
  );
}
